// 本地型別定義，避免共享檔案路徑問題

// 使用者相關型別
export interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  role: UserRole;
  enabled: boolean;
  adminNotes?: string;
  lastLoginAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export enum UserRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  ADMIN = 'ADMIN',
  USER = 'USER'
}

// 專案相關型別
export interface Project {
  id: string;
  name: string;
  key: string;
  description?: string;
  icon?: string;
  archived: boolean;
  allowPublicView: boolean;
  financeEnabled: boolean;
  trashRetentionDays: number;
  organizationType: OrganizationType;
  projectType: ProjectType;
  roleTemplateId?: string;
  createdAt: Date;
  updatedAt: Date;
  members: ProjectMember[];
  issues: Issue[];
  roleTemplate?: RoleTemplate;
  _count?: {
    issues: number;
    members: number;
  };
}

export interface ProjectMember {
  id: string;
  userId: string;
  projectId: string;
  roleId: string;
  user: User;
  project: Project;
  role: CustomRole;
  joinedAt: Date;
}

export enum OrganizationType {
  GENERAL_COMPANY = 'GENERAL_COMPANY',
  PROPERTY_MANAGEMENT = 'PROPERTY_MANAGEMENT',
  COMMUNITY_COMMITTEE = 'COMMUNITY_COMMITTEE',
  MIXED = 'MIXED'
}

export enum ProjectType {
  GENERAL = 'GENERAL',
  PROPERTY_MAINTENANCE = 'PROPERTY_MAINTENANCE',
  COMMUNITY_AFFAIRS = 'COMMUNITY_AFFAIRS',
  CONTRACTOR_MANAGEMENT = 'CONTRACTOR_MANAGEMENT',
  COMMITTEE_SUPERVISION = 'COMMITTEE_SUPERVISION',
  FINANCIAL_MANAGEMENT = 'FINANCIAL_MANAGEMENT'
}

// 自訂角色系統
export interface CustomRole {
  id: string;
  name: string;
  description?: string;
  color: string;
  isSystem: boolean;
  templateId?: string;
  createdAt: Date;
  updatedAt: Date;
  rolePermissions?: ProjectRolePermission[];
}

export interface RoleTemplate {
  id: string;
  name: string;
  description?: string;
  organizationType: OrganizationType;
  projectType: ProjectType;
  isSystem: boolean;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  roles: CustomRole[];
}

export interface ProjectRolePermission {
  id: string;
  roleId: string;
  permission: Permission;
  createdAt: Date;
}

// 議題相關型別
export interface Issue {
  id: string;
  key: string;
  title: string;
  description?: string;
  status: IssueStatus;
  customStatus?: string;
  priority: IssuePriority;
  position: number;
  archived: boolean;
  deleted: boolean;
  deletedAt?: Date;
  dueDate?: Date;
  projectId: string;
  reporterId: string;
  assigneeId?: string;
  startDate?: Date;
  endDate?: Date;
  createdAt: Date;
  updatedAt: Date;
  project: Project;
  reporter: User;
  assignee?: User;
  comments: Comment[];
  labels: Label[];
  activities: Activity[];
  boardStatus?: BoardStatus;
  customFieldValues?: CustomFieldValue[];
}

export enum IssueStatus {
  TODO = 'TODO',
  IN_PROGRESS = 'IN_PROGRESS',
  IN_REVIEW = 'IN_REVIEW',
  DONE = 'DONE'
}

export enum IssuePriority {
  LOWEST = 'LOWEST',
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  HIGHEST = 'HIGHEST'
}

// 留言相關型別
export interface Comment {
  id: string;
  content: string;
  issueId: string;
  authorId: string;
  createdAt: Date;
  updatedAt: Date;
  issue: Issue;
  author: User;
}

// 標籤相關型別
export interface Label {
  id: string;
  name: string;
  color: string;
  projectId: string;
  project: Project;
}

// 活動日誌相關型別
export interface Activity {
  id: string;
  type: string;        // 改為 string 以支援更多活動類型
  description?: string; // 活動描述（保留以保持相容性）
  details?: string;    // 活動詳情
  field?: string;      // 變更的欄位名稱
  oldValue?: string;   // 變更前的值
  newValue?: string;   // 變更後的值
  issueId: string;
  issueKey?: string;   // 議題鍵值
  userId: string;
  createdAt: Date | string;
  issue?: Issue;
  user?: User;
}

// 附件相關型別
export interface Attachment {
  id: string;
  originalName: string;
  filename: string;
  filepath: string;
  size: number;
  mimeType: string;
  uploadedById: string;
  issueId?: string;
  commentId?: string;
  createdAt: Date;
  updatedAt: Date;
  uploadedBy: User;
  issue?: Issue;
  comment?: Comment;
}

export enum ActivityType {
  CREATED = 'CREATED',
  UPDATED = 'UPDATED',
  STATUS_CHANGED = 'STATUS_CHANGED',
  ASSIGNED = 'ASSIGNED',
  COMMENTED = 'COMMENTED'
}

// 看板相關型別
export interface BoardColumn {
  id: string;
  title: string;
  status: IssueStatus;
  issues: Issue[];
}

export interface BoardStatus {
  id: string;
  name: string;
  color: string;
  position: number;
  projectId: string;
  isDefault: boolean;
  createdAt: Date;
  updatedAt: Date;
  _count?: {
    issues: number;
  };
}

// 權限相關型別
export enum Permission {
  // 系統層級權限
  MANAGE_USERS = 'MANAGE_USERS',
  MANAGE_SYSTEM_SETTINGS = 'MANAGE_SYSTEM_SETTINGS',
  VIEW_AUDIT_LOGS = 'VIEW_AUDIT_LOGS',
  DELETE_USERS = 'DELETE_USERS',
  MANAGE_ORGANIZATION = 'MANAGE_ORGANIZATION',
  MANAGE_TEMPLATES = 'MANAGE_TEMPLATES',
  
  // 專案層級權限  
  MANAGE_PROJECT = 'MANAGE_PROJECT',
  MANAGE_PROJECT_MEMBERS = 'MANAGE_PROJECT_MEMBERS',
  ARCHIVE_PROJECT = 'ARCHIVE_PROJECT',
  DELETE_PROJECT = 'DELETE_PROJECT',
  VIEW_ALL_PROJECTS = 'VIEW_ALL_PROJECTS',
  CREATE_PROJECT = 'CREATE_PROJECT',
  EDIT_PROJECT = 'EDIT_PROJECT',
  MANAGE_PROJECT_SETTINGS = 'MANAGE_PROJECT_SETTINGS',
  
  // 議題層級權限
  CREATE_ISSUE = 'CREATE_ISSUE',
  EDIT_ISSUE = 'EDIT_ISSUE',
  DELETE_ISSUE = 'DELETE_ISSUE',
  ARCHIVE_ISSUE = 'ARCHIVE_ISSUE',
  ASSIGN_ISSUE = 'ASSIGN_ISSUE',
  VIEW_ISSUE = 'VIEW_ISSUE',
  TRANSITION_ISSUE = 'TRANSITION_ISSUE',
  
  // 看板層級權限
  MANAGE_BOARD_STATUS = 'MANAGE_BOARD_STATUS',
  REORDER_BOARD_STATUS = 'REORDER_BOARD_STATUS',
  VIEW_BOARD = 'VIEW_BOARD',
  
  // 評論權限
  CREATE_COMMENT = 'CREATE_COMMENT',
  EDIT_COMMENT = 'EDIT_COMMENT',
  DELETE_COMMENT = 'DELETE_COMMENT',
  VIEW_COMMENT = 'VIEW_COMMENT',
  
  // 報告和監督權限
  VIEW_REPORTS = 'VIEW_REPORTS',
  VIEW_FINANCIAL_REPORTS = 'VIEW_FINANCIAL_REPORTS',
  VIEW_MAINTENANCE_REPORTS = 'VIEW_MAINTENANCE_REPORTS',
  VIEW_AUDIT_REPORTS = 'VIEW_AUDIT_REPORTS',
  EXPORT_REPORTS = 'EXPORT_REPORTS',
  
  // 物業管理特定權限
  MANAGE_MAINTENANCE_REQUESTS = 'MANAGE_MAINTENANCE_REQUESTS',
  ASSIGN_MAINTENANCE_TASKS = 'ASSIGN_MAINTENANCE_TASKS',
  VIEW_MAINTENANCE_STATUS = 'VIEW_MAINTENANCE_STATUS',
  APPROVE_MAINTENANCE_BUDGET = 'APPROVE_MAINTENANCE_BUDGET',
  MANAGE_CONTRACTOR_ASSIGNMENTS = 'MANAGE_CONTRACTOR_ASSIGNMENTS',
  
  // 管委會權限
  VIEW_COMMITTEE_REPORTS = 'VIEW_COMMITTEE_REPORTS',
  APPROVE_BUDGET_ITEMS = 'APPROVE_BUDGET_ITEMS',
  VIEW_FINANCIAL_OVERVIEW = 'VIEW_FINANCIAL_OVERVIEW',
  SUPERVISE_OPERATIONS = 'SUPERVISE_OPERATIONS',
  
  // 通知和溝通權限
  SEND_NOTIFICATIONS = 'SEND_NOTIFICATIONS',
  MANAGE_ANNOUNCEMENTS = 'MANAGE_ANNOUNCEMENTS',
  VIEW_ACTIVITY_LOGS = 'VIEW_ACTIVITY_LOGS',

  // 檔案和文件權限
  UPLOAD_DOCUMENTS = 'UPLOAD_DOCUMENTS',
  VIEW_ATTACHMENTS = 'VIEW_ATTACHMENTS',
  MANAGE_DOCUMENT_LIBRARY = 'MANAGE_DOCUMENT_LIBRARY',
  VIEW_SENSITIVE_DOCUMENTS = 'VIEW_SENSITIVE_DOCUMENTS',

  // 自訂欄位權限
  MANAGE_CUSTOM_FIELDS = 'MANAGE_CUSTOM_FIELDS',

  // 工作日誌權限
  CREATE_WORK_LOG = 'CREATE_WORK_LOG',
  EDIT_WORK_LOG = 'EDIT_WORK_LOG',
  DELETE_WORK_LOG = 'DELETE_WORK_LOG',
  VIEW_WORK_LOG = 'VIEW_WORK_LOG',
  EXPORT_WORK_LOG = 'EXPORT_WORK_LOG',
  
  // 全域工作日誌權限
  CREATE_GLOBAL_WORK_LOG = 'CREATE_GLOBAL_WORK_LOG',
  EDIT_GLOBAL_WORK_LOG = 'EDIT_GLOBAL_WORK_LOG',
  DELETE_GLOBAL_WORK_LOG = 'DELETE_GLOBAL_WORK_LOG',
  VIEW_GLOBAL_WORK_LOG = 'VIEW_GLOBAL_WORK_LOG',
  VIEW_ALL_GLOBAL_WORK_LOG = 'VIEW_ALL_GLOBAL_WORK_LOG',
  MANAGE_GLOBAL_WORK_LOG = 'MANAGE_GLOBAL_WORK_LOG'
}

// 權限群組相關型別
export interface PermissionGroup {
  id: string;
  name: string;
  description?: string;
  isSystem: boolean;
  createdAt: Date;
  updatedAt: Date;
  permissions: GroupPermission[];
  users: UserGroup[];
}

export interface GroupPermission {
  id: string;
  groupId: string;
  permission: Permission;
  createdAt: Date;
}

export interface UserGroup {
  id: string;
  userId: string;
  groupId: string;
  assignedAt: Date;
  user: User;
  group: PermissionGroup;
}

// 篩選器型別
export interface IssueFilters {
  assignee?: string;
  priority?: IssuePriority;
  labels?: string[];
  search?: string;
}

// 到期日篩選類型
export type DueDateFilterType = 'overdue' | 'today' | 'week' | 'custom' | null

export interface DueDateFilter {
  type: DueDateFilterType
  customRange?: {
    from: Date
    to: Date
  }
}

// 完整的看板篩選狀態
export interface BoardFilterState {
  // 標籤篩選
  labels: string[]
  labelLogic: 'AND' | 'OR'
  
  // 指派篩選
  assignees: string[]
  showMyIssues: boolean
  showUnassigned: boolean
  showCreatedByMe: boolean
  
  // 優先級篩選
  priorities: IssuePriority[]
  
  // 到期日篩選
  dueDateFilter: DueDateFilter
  
  // 搜尋
  searchQuery: string
  
  // UI 狀態
  isExpanded: boolean
  activeSection: string | null
}

// API 回應型別
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export * from './forms'
export * from './finance'

// 工作日誌相關型別
export enum WorkLogStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
  ARCHIVED = 'ARCHIVED'
}

export interface WorkLog {
  id: string;
  title: string;
  projectId: string;
  createdById: string;
  startDate: string;
  endDate: string;
  status: WorkLogStatus;

  // 基本資訊
  communityName?: string;
  managementCompany?: string;
  dutyStaff?: string;

  // 工作項目內容
  morningWorkItems?: any;
  afternoonWorkItems?: any;
  otherWorkItems?: any;
  
  createdAt: string;
  updatedAt: string;
  
  // 關聯資料
  project?: Project;
  createdBy?: User;
  selectedIssues?: WorkLogIssue[];
}

export interface WorkLogIssue {
  id: string;
  workLogId: string;
  issueId: string;
  sortOrder: number;
  createdAt: string;
  issue: Issue;
}

// 自訂欄位相關型別
export interface CustomField {
  id: string;
  name: string;
  key: string;
  type: 'TEXT' | 'NUMBER' | 'DATE' | 'SELECT';
  options?: string[] | null;
  required: boolean;
  isGlobal: boolean;
  createdAt: string;
  updatedAt: string;
  creator: {
    id: string;
    name: string;
    email: string;
  };
  _count?: {
    projects: number;
    values: number;
  };
}

export interface CustomFieldValue {
  id: string;
  issueId: string;
  fieldId: string;
  value?: string | null;
  numberValue?: number | null;
  dateValue?: string | null;
  field?: CustomField; // 可選，因為有時我們只有 fieldId
  createdAt: string;
  updatedAt: string;
  updatedBy?: string; // 新增 updatedBy 欄位
}
